/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { GetServerSideProps } from 'next'
import NextImage from 'next/image'
import { Constants } from '@/assets'
import { useTranslation, Trans } from 'next-i18next'
import useCurrentUser from '@/hooks/useCurrentUser'
import { useRouter } from 'next/router'
import { Routes } from '@/assets/configs'
import { lockScroll, unlockScroll } from '@/utils/scrollLock'

// Misc
import { useIsResponsive } from '@/hooks'

// Components
import PublicNavigation from '@/components/EMT/PublicNavigation'
import VariantImages from '@/assets/VariantImages'
import Filters, { FiltersHandle } from '@/components/pages/pet-sitter/search/Filters'

import { SEO, withPrivateRouteProps, withPublicRoute } from '@/components'
import { Navigation } from '@/components/EMT/Navigation'

import { styled } from '@mui/material/styles'
import {
  EmtButton,
  EmtClickBox,
  EmtDrawer,
  EmtHeader,
  EmtIcon,
  EmtMap,
  EmtMarker,
  EmtModal,
  EmtProgressBar,
  EmtSeparator,
  EmtTypography,
  MarkerList,
  // NacIcon,
  OpenMapIcon,
  // useScrollTransform,
} from 'emt-components-v2'
import MobileSwipeMenu from '@/components/PetSitter/MobileSwipeMenu'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useIsQueryOwner } from '@/hooks'
// import SearchPetSitterTipsModal from '@/components/PetSitter/SearchPetSitterTipsModal'
import FloatingProfileModal from '@/components/FloatingProfileModal'

import { Box, Container, Skeleton, Tabs, Tab } from '@mui/material'

import PetSittingNav from '@/components/PetSitter/PetSittingNav'
import PublicPetSittingNav from '@/components/PetSitter/PublicPetSittingNav'
import PetSitterCard from '@/components/PetSitter/PetSitterCard/PetSitterCard'
import PetsittersSelection from '@/components/PetSitter/PetsittersSelection/PetsittersSelection'

import NewSearch from '@/components/pages/pet-sitter/search/NewSearch'

import { expandInstancesAll } from '@/components/pages/sign-up-call-request/ReservationInfos'
import { transformToCartProducts } from '@/modules/PetSitter/tendersServiceHelper'

// API
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { FiltersValues, getSearchAsync } from '@/modules/PetSitter/searchServiceHelper'
import { getClientIp, fetchIpApi, PARIS_CENTER } from '@/utils/search/ipGeo'

// Types
import getEMTServerSideProps, { EMTServerSideProps } from '@/helpers/getEMTServerSideProps'
import { PetSitterProfile, PetSitterState } from '@/data/PetSitter/PetSitterProfile'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'

// Store
import { shallow } from 'zustand/shallow'
import { defaultSlots, useNewRequestCartStore } from '@/store/newRequestCartStore'
import { storeToFilters, filtersToStorePatch } from '@/helpers/search/searchFilters'
import {
  getSingleQueryParam,
  hasSearchQuery,
  isCompleteSearchQuery,
  parseSearchQuery,
  searchQueryToStorePatch,
} from '@/helpers/search/searchQuery'
import { ReservationDates } from '@/components/PetSitter/ReservationCard/ReservationDates'
import { usePetsittingMode } from '@/store/usePetsittingMode'

import { services as serviceConfigs } from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'
import SignUpModal from '@/components/Modals/SignUpModal'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'

type OrderFiltersNames =
  | 'base'
  | 'service'
  | 'period'
  | 'pets'
  | 'distance'
  | 'price'
  | 'petCount'
  | 'petSizes'
  | 'features'

// Add this helper function before the component
export function getPetsSummary(
  petsToComplete: { slug: string; number: number }[] = [],
  t: (key: string, opts?: any) => string,
) {
  if (!petsToComplete || petsToComplete.length === 0) return t('search.select') || 'Sélectionner'
  return petsToComplete
    .filter((p) => p.number > 0)
    .map((p) => {
      let label = ''
      if (p.slug === 'dog') label = p.number > 1 ? 'chiens' : 'chien'
      else if (p.slug === 'cat') label = p.number > 1 ? 'chats' : 'chat'
      else label = p.number > 1 ? 'autres' : 'autre'
      return `${p.number} ${label}`
    })
    .join(', ')
}

export function getPetsNumber(petsToComplete: { slug: string; number: number }[] = []) {
  return petsToComplete.reduce((total, pet) => total + (pet.number || 0), 0)
}

export const MarkerContainer = styled(Box)(() => ({
  cursor: 'pointer',
  '&.mapboxgl-marker': {
    zIndex: 1,
    '&.permanent': {
      zIndex: '9 !important',
    },
    '&.active': {
      zIndex: '8 !important',
    },
  },
  '& img': { cursor: 'pointer' },
  '& .img-marker': {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '100%',
    transition: 'all 0.2s linear',
    width: 42,
    height: 42,
    boxShadow: '0px 4px 14px 2px rgba(0, 0, 0, 0.19), 0px 4px 24px rgba(0, 0, 0, 0.08)',
    '& img': {
      transition: 'all 0.2s linear',
      opacity: 0.6,
      width: '100%',
      height: '100%',
    },
    '&:hover': {
      '& img': { opacity: 1 },
    },
    '&.active': {
      '& img': { opacity: 1 },
    },
  },
  '&.active': {
    '& .img-marker': {
      '& img': { opacity: 1 },
    },
  },
  '&.permanent': {
    '& .img-marker': {
      width: 50,
      height: 50,
      '& img': { opacity: 1, width: '100%', height: '100%' },
    },
  },
}))

// Adjust the services to have correct slugs
// const services = [
//   { slug: 'sittingAtPetsitterHome', title: 'Garde chez le pet sitter', selected: false },
//   { slug: 'sittingAtCustomerHome', title: 'Garde chez le propriétaire', selected: false },
//   { slug: 'walk', title: 'Promenades', selected: false },
//   { slug: 'homeVisit', title: 'Visites à domicile', selected: false },
// ]

// const pets = [
//   { slug: 'dog', title: 'Chien', selected: false, iconClass: 'emt-dog' },
//   { slug: 'cat', title: 'Chat', selected: false, iconClass: 'emt-cat' },
//   {
//     slug: 'nac',
//     title: 'Autre',
//     selected: false,
//     icon: (props: any) => <NacIcon width="16px" height="15px" {...props} />,
//   },
// ]

const distanceCorrespondingValues = {
  percentToValue: {
    0: 0,
    10: 1,
    20: 2,
    30: 3,
    40: 4,
    50: 5,
    60: 10,
    70: 15,
    80: 20,
    90: 30,
    100: 50,
  },
}

function degreesToRadians(degreeValue: number) {
  return (degreeValue * Math.PI) / 180
}

// Extract distances from percentToValue and remove duplicates
const distances = Array.from(new Set(Object.values(distanceCorrespondingValues.percentToValue))).sort((a, b) => a - b)

// Function to find the closest distance
const getClosestDistance = (num: number, arr: number[]) => {
  return arr.reduce((prev, curr) => (Math.abs(curr - num) < Math.abs(prev - num) ? curr : prev))
}

const C_EARTH = 40075016.686

const calculateMapZoom = (latitude: number, distanceMax: number, mapSize: number) => {
  const sPixel = (distanceMax * 1000) / (mapSize * 0.5)
  const power = (C_EARTH * Math.cos(degreesToRadians(latitude))) / (sPixel * 512)

  return Math.log(power) / Math.log(2)
}

const calculateDistance = (latitude: number, zoom: number, mapSize: number) => {
  // Calculation based on https://wiki.openstreetmap.org/wiki/Zoom_levels (Mapbox use 512px tiles instead of 256)
  const sTile = (C_EARTH * Math.cos(degreesToRadians(latitude))) / Math.pow(2, zoom)
  const sPixel = sTile / 512

  // Divide by 2 (to get distance to the center)
  const pixelsCount = mapSize * 0.5

  // Divide by 1000 to have kilometers
  return (sPixel * pixelsCount) / 1000
}

export type MapFinderCoordinates = { lng: number; lat: number }

type Props = withPrivateRouteProps & EMTServerSideProps & { defaultAddress?: any }

// Minimal Hydra typing
type HydraPage = {
  'hydra:member'?: PetSitterProfile[]
  'hydra:totalItems'?: number
}

const hasMinimumInfos = (args: {
  hasHydrated: boolean
  selectedService: string | null
  formSlotsByService: Record<string, { id: string; data: any | null }[]>
  petsToComplete: { slug: string; number: number }[]
  address?: any
}) => {
  const { hasHydrated, selectedService, formSlotsByService, petsToComplete, address } = args

  if (!hasHydrated) return false

  const hasSelectedService = !!selectedService

  const hasFormSlotForService =
    !!selectedService &&
    Array.isArray(formSlotsByService?.[selectedService]) &&
    formSlotsByService[selectedService].some((s) => s?.data != null) // <- au moins un slot “rempli”
  // si tu veux juste “au moins un slot”, remplace par: .length > 0

  const hasAtLeastOnePet = (petsToComplete ?? []).some((p) => (p?.number ?? 0) > 0)

  const hasAddress =
    !!address &&
    // adapte selon la shape exacte de ton address persisté
    (typeof address.latitude === 'number' || typeof address.lat === 'number') &&
    (typeof address.longitude === 'number' || typeof address.lng === 'number')

  return hasSelectedService && hasFormSlotForService && hasAtLeastOnePet && hasAddress
}

const SearchPage: React.FC<Props> = ({ url, title, defaultAddress }) => {
  const t = useTranslation('petsitter').t
  const { isPetsittingMode } = usePetsittingMode()
  const queryClient = useQueryClient()
  const { currentUser } = useCurrentUser()
  const [, type] = useIsQueryOwner(true)
  const router = useRouter()

  const disableNextCenterAnimationRef = useRef(false)
  const [disableNextCenterAnimation, setDisableNextCenterAnimation] = useState(false)

  const [newSearchLoading, setNewSearchLoading] = useState(false)
  const pendingCloseAfterFlyRef = useRef(false)

  const roundLatLng = (n: number | undefined) => (n ? parseFloat(n.toFixed(4)) : 0)

  const handleNewSearchNext = async (values: FiltersValues) => {
    // detect if the map will actually fly (coords changed)
    const nextLat = values.address?.latitude
    const nextLng = values.address?.longitude

    const willFly =
      typeof nextLat === 'number' &&
      typeof nextLng === 'number' &&
      (roundLatLng(nextLat) !== roundLatLng(coordinates.lat) || roundLatLng(nextLng) !== roundLatLng(coordinates.lng))

    setNewSearchLoading(true)
    pendingCloseAfterFlyRef.current = willFly

    disableNextCenterAnimationRef.current = true
    setDisableNextCenterAnimation(true)

    await handleSearchClick(values) // updates filters/searchParams => triggers EmtMap flyTo via props.lng/lat

    setShowNewSearch(false)
    setNewSearchLoading(false)

    // Send gtm event when submit new search
    gtmSendCustomEvent(currentUser ? 'member_search_form_sent_sst' : 'member_public_search_form_sent_sst', {
      member_role_variable: 'maitre',
      member_id_variable: currentUser?.id || 'unknown',
      brand_variable: Constants.storageID,
      website_variable: Constants.rootUrl,
    })
  }

  const [shortFilterValue, setShortFilterValue] = useState<OrderFiltersNames>('base')

  useEffect(() => {
    if (type == 'pro' && currentUser) {
      router.push(Routes.Pro.Dashboard)
    }
    if (type === 'borrower' && currentUser) {
      router.push(Routes.Home)
    }
  }, [currentUser, type])

  // If no currentUser, set 'type' query param to 'dogowner' in the URL
  useEffect(() => {
    if (!currentUser && typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      if (url.searchParams.get('type') !== 'dogowner') {
        url.searchParams.set('type', 'dogowner')
        window.history.replaceState({}, '', url.toString())
      }
    }
  }, [currentUser])

  const [isMobile] = useIsResponsive()
  const [count, setCount] = useState(0)
  const [childWidth, setChildWidth] = useState('')
  const [showMap, setShowMap] = useState(isMobile ? false : true)
  const [signUpModalOpen, setSignUpModalOpen] = useState<boolean>(false)
  const [selectedPetSitterId, setSelectedPetSitterId] = useState<string | number | null>(null)
  const petSitterRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const petsittersIds = useNewRequestCartStore((s) => s.petsittersIds)

  const filtersRef = useRef<FiltersHandle | null>(null)

  // [HOOK] Floating Mobile Profile
  const floating = useFloatingProfile({
    defaultProfileKind: ProfileKind.PetSitting,
    publicProfile: !currentUser,
  })

  const setStore = useNewRequestCartStore(
    (s) => ({
      setAddress: s.setAddress,
      setDistance: s.setDistance,
      setSelectedService: s.setSelectedService,
      setPetsToComplete: s.setPetsToComplete,
    }),
    shallow,
  )

  const applyPatchToStore = (patch: ReturnType<typeof filtersToStorePatch>) => {
    if ('address' in patch) setStore.setAddress(patch.address)
    if ('distance' in patch && patch.distance) setStore.setDistance(patch.distance)
    if ('selectedService' in patch) setStore.setSelectedService(patch.selectedService ?? null)
    if ('petsToComplete' in patch && patch.petsToComplete) setStore.setPetsToComplete(patch.petsToComplete)
  }

  const { hasHydrated, address, selectedService, petsToComplete, formSlotsByService, distance, hasCompleteSearch } =
    useNewRequestCartStore(
      (s) => ({
        hasHydrated: s.hasHydrated,
        address: s.address,
        selectedService: s.selectedService,
        petsToComplete: s.petsToComplete,
        formSlotsByService: s.formSlotsByService,
        distance: s.distance,
        hasCompleteSearch: s.hasCompleteSearch,
      }),
      shallow,
    )

  const [newSearchInitialStep, setNewSearchInitialStep] = useState<boolean>(true)
  // const [showNewSearch, setShowNewSearch] = useState(!hasCompleteSearch)
  const [showNewSearch, setShowNewSearch] = useState(isMobile ? true : false)

  const searchQuery = useMemo(() => parseSearchQuery(router.query), [router.query])

  useEffect(() => {
    if (!showNewSearch) return

    // si un service valide est dans l’URL, on garde l’étape 2
    setNewSearchInitialStep(!searchQuery.service)
  }, [showNewSearch, searchQuery.service])

  useEffect(() => {
    if (!showNewSearch || !isMobile) return
    lockScroll()
    return () => unlockScroll()
  }, [showNewSearch, isMobile])

  useEffect(() => {
    if (!signUpModalOpen) return
    lockScroll()
    return () => unlockScroll()
  }, [signUpModalOpen])

  useEffect(() => {
    if (!signUpModalOpen) return

    // Send gtm event when open signup modal
    gtmSendCustomEvent('member_registration_popup_view_sst', {
      member_role_variable: 'maitre',
      member_id_variable: 'unknown',
      brand_variable: Constants.storageID,
      website_variable: Constants.rootUrl,
    })
  }, [signUpModalOpen])

  const showNewSearchRef = useRef(showNewSearch)
  useEffect(() => {
    showNewSearchRef.current = showNewSearch
  }, [showNewSearch])

  const minInfos = useMemo(
    () =>
      hasMinimumInfos({
        hasHydrated,
        selectedService,
        formSlotsByService: formSlotsByService as any,
        petsToComplete,
        address,
      }),
    [hasHydrated, selectedService, formSlotsByService, petsToComplete, address],
  )

  // 👉 important: on initialise showNewSearch UNE fois, après hydratation
  const didInitShowNewSearch = useRef(false)

  useEffect(() => {
    if (didInitShowNewSearch.current) return
    if (!hasHydrated) return
    if (!router.isReady) return

    // Arrivée depuis un formulaire externe (WordPress) : résultats directs si la recherche est complète,
    // sinon formulaire ouvert et pré-rempli
    if (searchQuery.source) {
      setShowNewSearch(!isCompleteSearchQuery(searchQuery))
      didInitShowNewSearch.current = true
      return
    }

    const skipParam = getSingleQueryParam(router.query.skipNewSearch)
    const shouldSkipNewSearch =
      hasCompleteSearch && (skipParam === '' || ['1', 'true', 'yes'].includes(String(skipParam).toLowerCase()))

    setShowNewSearch(shouldSkipNewSearch ? false : isMobile ? true : !minInfos)
    didInitShowNewSearch.current = true
  }, [hasHydrated, router.isReady, router.query.skipNewSearch, hasCompleteSearch, isMobile, minInfos, searchQuery])

  const initialFiltersValues = useMemo(
    () =>
      storeToFilters({
        address: address || currentUser?.settings.address,
        distance,
        selectedService,
        petsToComplete,
        formSlotsByService,
      }),
    [address, distance, selectedService, petsToComplete, formSlotsByService, currentUser],
  )

  const [filtersValues, setFiltersValues] = useState<FiltersValues>(initialFiltersValues)
  const [searchParams, setSearchParams] = useState<FiltersValues>(initialFiltersValues)

  const didInitFromQuery = useRef(false)

  // Used to store current map location when dragging, before search
  const [coordinates, setCoordinates] = useState<MapFinderCoordinates>({
    lng: (filtersValues.address?.longitude as number) ?? 0,
    lat: (filtersValues.address?.latitude as number) ?? 0,
  })

  // Init search from URL (service, address, pets, dates), otherwise fallback on IP address for visitors
  useEffect(() => {
    if (didInitFromQuery.current) return
    if (!hasHydrated) return
    if (!router.isReady) return

    // priorité absolue à l'URL
    if (hasSearchQuery(searchQuery)) {
      const { formSlotsByService: currentSlots } = useNewRequestCartStore.getState()
      useNewRequestCartStore.setState(
        searchQueryToStorePatch(searchQuery, { current: currentSlots, blank: defaultSlots() }),
      )

      const nextState = useNewRequestCartStore.getState()
      const nextFilters = storeToFilters({
        ...nextState,
        address: nextState.address || currentUser?.settings.address,
      })
      setFiltersValues(nextFilters)
      setSearchParams(nextFilters)

      didInitFromQuery.current = true
      return
    }

    if (address) {
      didInitFromQuery.current = true
      return
    }

    // Only use defaultAddress if there is NO currentUser
    if (!currentUser) {
      const chosenAddress = defaultAddress
      if (!chosenAddress) return

      setStore.setAddress(chosenAddress as any)
      setFiltersValues((prev) => ({ ...prev, address: chosenAddress as any }))
      setSearchParams((prev) => ({ ...prev, address: chosenAddress as any }))
      setCoordinates({ lat: chosenAddress.latitude, lng: chosenAddress.longitude })
    }

    didInitFromQuery.current = true
  }, [hasHydrated, router.isReady, searchQuery, address, defaultAddress, setStore, currentUser])

  const [mapZoom, setMapZoom] = useLocalStorage('petSitter_mapZoom', 9.8)

  // Add this useEffect to calculate initial count
  useEffect(() => {
    const calculateActiveFilters = (filters: FiltersValues) => {
      let c = 0
      if (filters.address) c++
      if (filters.services) c++
      if (filters.period?.start || filters.period?.end) c++
      if (filters.pets && filters.pets.length > 0) c++
      if (filters.petSize && filters.petSize.length > 0) c++
      if (filters.features && filters.features.length > 0) c++
      if (
        filters.distance &&
        (filters.distance.min !== initialFiltersValues.distance?.min ||
          filters.distance.max !== initialFiltersValues.distance?.max)
      ) {
        c++
      }
      setCount(c)
    }

    calculateActiveFilters(filtersValues)
  }, [filtersValues, initialFiltersValues])

  // SCROLL HIDE SECTION
  const [hideStage, setHideStage] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const petSittersContainerRef = useRef<HTMLDivElement>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef(null)
  const navigationRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const petsittersSelectionRef = useRef<HTMLDivElement>(null)

  const [containerHeight, setContainerHeight] = useState(`calc(100vh - 40px - 63px)`)

  const [activeFilterSidebar, setActiveFilterSidebar] = useState(false)
  const toggleActiveFilter = () => setActiveFilterSidebar((prev) => !prev)

  const [activeSearchButton, setActiveSearchButton] = useState(false)
  const [markers, setMarkers] = useState<MarkerList>([])

  useEffect(() => {
    const resetBody = () => {
      document.body.style.overflow = 'initial'
      document.body.style.position = ''
      document.body.style.width = ''
    }

    const onRouteChangeStart = () => resetBody()

    router.events.on('routeChangeStart', onRouteChangeStart)
    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
      resetBody()
    }
  }, [router.events])

  const searchTriggeredByUser = useRef(false)

  useEffect(() => {
    if (filtersValues.address) {
      setCoordinates({
        lng: filtersValues.address.longitude,
        lat: filtersValues.address.latitude,
      })
    }
  }, [filtersValues.address])

  // const { transformStyles } = useScrollTransform({
  //   translateY: { hidden: '124px', visible: '0' },
  // })

  const dateIntervals = expandInstancesAll(formSlotsByService[selectedService ?? ''] ?? [], selectedService ?? '')
  const dates = selectedService
    ? transformToCartProducts(selectedService, dateIntervals)?.map(({ offer, ...rest }) => ({
        productSlug: offer,
        ...rest,
      }))
    : undefined

  // Initialize petSitterStates as an empty array
  const [petSitterStates, setPetSitterStates] = useState<PetSitterState[]>([])

  // --- Infinite Query ---
  const queryKey = useMemo(() => ['searchResultsInfinite', searchParams] as const, [searchParams])

  const {
    data: infiniteData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery<HydraPage>(
    queryKey,
    // IMPORTANT: default pageParam to 1 (since v4 has no initialPageParam)
    ({ pageParam = 1 }) => getSearchAsync(searchParams, pageParam) as Promise<HydraPage>,
    {
      getNextPageParam: (lastPage, allPages) => {
        const total = (lastPage?.['hydra:totalItems'] ?? allPages?.[0]?.['hydra:totalItems'] ?? 0) as number

        const fetched = allPages.reduce((acc, p) => acc + (p?.['hydra:member']?.length ?? 0), 0)

        if (!total) return undefined
        return fetched < total ? allPages.length + 1 : undefined
      },
      refetchOnWindowFocus: false,
    },
  )

  const hasSentSearchViewEventRef = useRef(false)

  // Send gtm event when see search page with new search closed
  useEffect(() => {
    if (showNewSearch || hasSentSearchViewEventRef.current) return

    gtmSendCustomEvent(currentUser ? 'member_search_view_sst' : 'member_public_search_view_sst', {
      member_role_variable: 'maitre',
      member_id_variable: currentUser?.id || 'unknown',
      brand_variable: Constants.storageID,
      website_variable: Constants.rootUrl,
    })

    hasSentSearchViewEventRef.current = true
  }, [showNewSearch])

  const lastNonEmptyRef = useRef<PetSitterProfile[]>([])

  const flatMembers = useMemo(() => {
    const pages = infiniteData?.pages ?? []
    const next = pages.flatMap((p) => p?.['hydra:member'] ?? [])

    if (next.length > 0) {
      lastNonEmptyRef.current = next
      return next
    }

    // new search started: keep previous list until the new one arrives
    return lastNonEmptyRef.current
  }, [infiniteData])

  const totalItems = useMemo(() => {
    const pages = infiniteData?.pages ?? []
    return (pages?.[0]?.['hydra:totalItems'] ?? 0) as number
  }, [infiniteData])

  // keep old “onSuccess” behavior (merge pages + checked flag)
  useEffect(() => {
    const newStates: PetSitterState[] = flatMembers.map((petSitter) => ({
      ...(petSitter as any),
      checked: false,
    }))
    setPetSitterStates(newStates)

    // stop “search area” button loader when query completes after user action
    if (searchTriggeredByUser.current && !isFetching) {
      setActiveSearchButton(false)
      searchTriggeredByUser.current = false
    }
  }, [flatMembers, isFetching])

  // Markers from states
  useEffect(() => {
    const newMarkers: EmtMarker[] = petSitterStates
      .filter((petSitter) => (petSitter as any).latitude && (petSitter as any).longitude)
      .map((petSitter) => ({
        id: (petSitter as any).uuid,
        coordinates: [(petSitter as any).longitude, (petSitter as any).latitude],
        component: generateMarkerComponent((petSitter as any).uuid),
        isActive: false,
      }))

    setMarkers(newMarkers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petSitterStates])

  // Show map only when everything is loaded
  useEffect(() => {
    if (
      !isMobile &&
      containerRef.current &&
      navigationRef.current &&
      titleRef.current &&
      petsittersSelectionRef.current
    ) {
      setShowMap(true)
    }
  }, [isMobile])

  const [resizeTrigger] = useState<number>(0)
  const [gridLayout] = useState<'1fr 1fr'>('1fr 1fr')

  useEffect(() => {
    if (typeof window !== 'undefined') document.body.style.overflow = 'hidden'
    if (showNewSearch) document.body.style.overflow = 'initial'
    if (isMobile && !showNewSearch) document.body.style.overflow = 'initial'
    return () => {
      document.body.style.overflow = 'initial'
    }
  }, [showNewSearch])

  // WITH ANIMATION FRAME
  useEffect(() => {
    if (isMobile) return
    const navigationEl = navigationRef.current
    const titleEl = titleRef.current
    const containerEl = containerRef.current
    const petSittersContainerEl = petSittersContainerRef.current
    const mapContainerEl = mapContainerRef.current
    const petsittersSelectionEl = petsittersSelectionRef.current

    const navHeight = navigationEl?.getBoundingClientRect().height ?? 0
    const titleHeight = titleEl?.getBoundingClientRect().height ?? 0
    const petsittersSelectionHeight = petsittersSelectionEl?.getBoundingClientRect().height ?? 0

    let isAnimating = false

    const visibleNavStyle = { marginTop: '0' }
    const hiddenNavStyle = { marginTop: `-${navHeight}px` }
    const visibleTitleStyle = { marginTop: '0', opacity: 1 }
    const hiddenTitleStyle = { marginTop: `-${titleHeight}px`, opacity: 0 }
    const scrolledContainerStyle = { marginTop: '-250px' }
    const unscrolledContainerStyle = { marginTop: '-209px' }

    setContainerHeight(`calc(100vh - ${titleHeight}px - ${petsittersSelectionHeight}px + 38px)`)

    const applyStyles = (stage: number) => {
      if (isAnimating) return
      isAnimating = true

      if (navigationEl && titleEl && containerEl) {
        switch (stage) {
          case 0:
            Object.assign(navigationEl.style, visibleNavStyle, { transition: 'all 0.3s ease, opacity 0s ease' })
            Object.assign(titleEl.style, visibleTitleStyle, { transition: 'all 0.3s ease, opacity 0.3s ease 0.2s' })
            Object.assign(containerEl.style, unscrolledContainerStyle, { transition: 'all 0.3s ease' })
            break
          case 1:
            Object.assign(navigationEl.style, hiddenNavStyle, { transition: 'all 0.3s ease, opacity 0s ease' })
            Object.assign(titleEl.style, hiddenTitleStyle, { transition: 'all 0.3s ease, opacity 0s ease' })
            Object.assign(containerEl.style, unscrolledContainerStyle, { transition: 'all 0.3s ease' })
            break
          case 2:
            Object.assign(navigationEl.style, hiddenNavStyle, { transition: 'all 0.3s ease, opacity 0s ease' })
            Object.assign(titleEl.style, hiddenTitleStyle, { transition: 'all 0.3s ease, opacity 0s ease' })
            Object.assign(containerEl.style, scrolledContainerStyle, { transition: 'all 0.3s ease' })
            break
          default:
            break
        }
      }

      setTimeout(() => {
        isAnimating = false
      }, 300)
    }

    applyStyles(hideStage)

    const handleWheel = (event: WheelEvent) => {
      const isInFilters = (event.target as Element)?.closest('[data-filters-container]')
      if (isInFilters) return

      const isInModalTips = (event.target as Element)?.closest('[data-modal-tips-container]')
      if (isInModalTips) return

      if (signUpModalOpen) return

      const containerScrollTop = petSittersContainerEl?.scrollTop ?? 0
      const isAtTop = containerScrollTop === 0

      if (petSittersContainerEl && petSittersContainerEl.contains(event.target as Node)) {
        if (isAtTop && event.deltaY < 0) {
          setHideStage((prev) => Math.max(prev - 1, 0))
        } else if (event.deltaY > 0) {
          setHideStage(2)
        }
      } else if (mapContainerEl && mapContainerEl.contains(event.target as Node)) {
        setHideStage(2)
      } else {
        const isScrollingDown = event.deltaY > 0
        setHideStage((prev) => (isScrollingDown ? Math.min(prev + 1, 2) : Math.max(prev - 1, 0)))
      }
    }

    if (!showNewSearch) window.addEventListener('wheel', handleWheel)

    return () => window.removeEventListener('wheel', handleWheel)
  }, [hideStage, isMobile, showNewSearch, signUpModalOpen])

  // window resize listener to setChildWidth
  useEffect(() => {
    const updateChildWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const newWidth = `${(containerWidth - 50) / 2}px`
        setChildWidth(newWidth)
      }
    }
    updateChildWidth()
    window.addEventListener('resize', updateChildWidth)
    return () => window.removeEventListener('resize', updateChildWidth)
  }, [])

  const generateMarkerComponent = (id: number | string) => {
    return (
      <MarkerContainer id={id as string} className="marker-container">
        <Box
          className={`img-marker active-icon`}
          onClick={() => {
            handleMarkerClick(id)
          }}
          sx={{ '& img': { cursor: 'pointer' } }}
        >
          <NextImage src={VariantImages.MapPinSitterActive} quality={100} alt={''} />
        </Box>
      </MarkerContainer>
    )
  }

  const [selectedPetSitter, setSelectedPetSitter] = useState<PetSitterState | null>(null)

  const handleMarkerClick = (id: number | string) => {
    const htmlMarkers = document.getElementsByClassName('marker-container')

    Array.prototype.forEach.call(htmlMarkers, (marker) => {
      if (marker.id === id) {
        marker.classList.add('permanent')
        marker.classList.add('active')
      } else {
        marker.classList.remove('active')
        marker.classList.remove('permanent')
      }
    })

    setSelectedPetSitterId(id)

    if (isMobile) {
      const s = petSitterStates.find((ps: any) => (ps as any).uuid === id)
      setSelectedPetSitter(s || null)
    } else {
      const cardElement = petSitterRefs.current[id as any]
      if (cardElement) cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const [zoomDistance, setZoomDistance] = useState(
    filtersValues.distance?.max || initialFiltersValues.distance?.max || 5,
  )

  const handleCardHover = (petSitterId: string) => {
    setSelectedPetSitterId(petSitterId)

    const htmlMarkers = document.getElementsByClassName('marker-container')
    Array.prototype.forEach.call(htmlMarkers, (marker) => {
      if (marker.id === petSitterId) {
        marker.classList.add('active')
        marker.classList.add('permanent')
      } else {
        marker.classList.remove('active')
        marker.classList.remove('permanent')
      }
    })
  }

  // Calculate map zoom based on address, distance and map size
  useEffect(() => {
    if (filtersValues.distance?.max && filtersValues.address && mapContainerRef.current) {
      const newMapZoom = calculateMapZoom(
        filtersValues.address.latitude,
        filtersValues.distance.max,
        Math.min(mapContainerRef.current.clientHeight, mapContainerRef.current.clientWidth),
      )
      setMapZoom(newMapZoom)
    }
  }, [filtersValues.distance?.max, filtersValues.address, setMapZoom])

  const handleMapDragEnd = (lng: number, lat: number) => setCoordinates({ lng, lat })

  /** Handle map zoom change to calculate the closest distance we need to set in filters */
  const handleMapZoom = (zoom: number) => {
    let newDistance: number

    if (filtersValues.address && mapContainerRef.current) {
      newDistance = calculateDistance(
        filtersValues.address.latitude,
        zoom,
        Math.min(mapContainerRef.current.clientWidth, mapContainerRef.current.clientHeight),
      )
    } else if (isMobile) {
      const B_mobile = 1.832
      newDistance = 0.07 * Math.pow(B_mobile, 20 - zoom) * 0.8
    } else {
      const B_desktop = 1.678
      newDistance = 0.1 * Math.pow(B_desktop, 20 - zoom) * 0.8
    }

    newDistance = getClosestDistance(newDistance, distances)
    setZoomDistance(newDistance)
  }

  const onMapDragEnd = (lng: number, lat: number) => {
    if (!searchTriggeredByUser.current) setActiveSearchButton(true)
    handleMapDragEnd(lng, lat)
  }

  const onMapZoom = (zoom: number, coords: { lat: number; lng: number }) => {
    if (!searchTriggeredByUser.current && Math.round(zoom * 100) !== Math.round(mapZoom * 100)) {
      setActiveSearchButton(true)
      handleMapZoom(zoom)
      handleMapDragEnd(coords.lng, coords.lat)
    }
  }

  const calculateActiveFilters = (filters: FiltersValues) => {
    let c = 0
    if (filters.address) c++
    if (filters.services) c++
    if (filters.period?.start || filters.period?.end) c++
    if (filters.pets && filters.pets.length > 0) c++
    if (filters.petSize && filters.petSize.length > 0) c++
    if (filters.features && filters.features.length > 0) c++
    if (
      filters.distance &&
      (filters.distance.min !== initialFiltersValues.distance?.min ||
        filters.distance.max !== initialFiltersValues.distance?.max)
    ) {
      c++
    }
    setCount(c)
  }

  const resetResults = () => {
    setPetSitterStates([])
    setMarkers([])
    setSelectedPetSitterId(null)
    setSelectedPetSitter(null)
  }

  const scrollPetSittersToTop = (behavior: ScrollBehavior = 'auto') => {
    const el = petSittersContainerRef.current
    if (!el) return

    // Firefox fallback
    el.scrollTop = 0
    // Standard
    el.scrollTo({ top: 0, behavior })
  }

  const handleFiltersValuesChange = (newValues: Partial<FiltersValues>) => {
    const updated = { ...filtersValues, ...newValues }
    setFiltersValues(updated)
    calculateActiveFilters(updated)

    applyPatchToStore(filtersToStorePatch(newValues))
  }

  const handleSearchParamsChange = (newValues: Partial<FiltersValues>) => {
    searchTriggeredByUser.current = true
    resetResults()

    const updated = { ...searchParams, ...newValues }

    setFiltersValues(updated)
    setSearchParams(updated)

    applyPatchToStore(filtersToStorePatch(newValues))

    // setActiveFilterSidebar(false)
    calculateActiveFilters(updated)
    scrollPetSittersToTop()

    queryClient.removeQueries({ queryKey: ['searchResultsInfinite'] })
  }

  const handleSearchClick = async (submitted?: FiltersValues) => {
    searchTriggeredByUser.current = true

    const next = submitted ?? filtersValues

    setFiltersValues(next)
    setSearchParams(next)

    setActiveFilterSidebar(false)
    scrollPetSittersToTop()

    await refetch()
  }

  const onSearchAreaClick = () => {
    searchTriggeredByUser.current = true

    scrollPetSittersToTop('auto')

    const newFiltersValues = {
      ...filtersValues,
      address: { latitude: coordinates.lat, longitude: coordinates.lng },
      distance: {
        ...filtersValues.distance,
        max: zoomDistance,
        min: filtersValues.distance?.min ?? 0,
      },
    }

    resetResults()
    setSearchParams(newFiltersValues)
    setFiltersValues(newFiltersValues)
    setActiveFilterSidebar(false)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollPetSittersToTop('auto')
      })
    })
  }

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }

  const hasMore = !!hasNextPage

  // const modalFilters = {
  //   address: searchParams.address,
  //   period: searchParams.period,
  //   services: searchParams.services,
  //   pets: searchParams.pets,
  // }

  // const handleModalFiltersChange = (newFilters: FiltersValues) => {
  //   handleSearchParamsChange(newFilters)
  // }

  const [tabValue, setTabValue] = useState(0)

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  return (
    <>
      <SEO url={url} subtitle={title} />
      <Box
        sx={{
          filter: activeFilterSidebar ? 'brightness(0.5)' : 'none',
          pointerEvents: activeFilterSidebar ? 'none' : 'auto',
        }}
      >
        <Box ref={navigationRef} sx={{ overflow: 'hidden' }}>
          {currentUser ? <Navigation /> : <PublicNavigation petsitter from="petsitter-search" />}
        </Box>

        {/* Nav petsitting */}
        {currentUser && !isMobile && !isPetsittingMode ? (
          <PetSittingNav />
        ) : (
          // <></>
          !isMobile &&
          !isPetsittingMode && (
            <PublicPetSittingNav
              title={
                <EmtTypography
                  variant="body2"
                  fontWeight="600"
                  color="#fff"
                  sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}
                >
                  Recherche Pet Sitter
                </EmtTypography>
              }
            />
          )
        )}
        {/* END Nav petsitting */}

        {/* Search infos */}
        {!isMobile && (
          <Box
            sx={{
              background: `url('/theme/header-pattern.png'), #762D8A`,
              backgroundColor: '#762D8A',
              backgroundRepeat: 'repeat',
              backgroundSize: '80%',
              width: '100%',
              paddingBottom: '38px',
              position: 'absolute',
              zIndex: 1201,
              display: showNewSearch ? 'block' : 'none',
              maxHeight: '450px',
              '&::after': {
                content: "''",
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(157.28deg, rgba(138, 53, 162, 0) 0.69%, #8A35A2 100%)',
                zIndex: -1,
              },
            }}
          >
            <Container
              sx={{
                paddingTop: isMobile ? '0' : '30px',
                paddingBottom: isMobile ? '240px' : 0,
                display: 'flex',
                flexDirection: 'column',
                rowGap: isMobile ? '15px' : '30px',
                position: 'relative',
                maxHeight: '100%',
              }}
            >
              {selectedService && !newSearchInitialStep && (
                <Box sx={{ display: 'flex', gap: '17px', alignItems: 'center' }}>
                  <Box
                    onClick={() => setStore.setSelectedService(null)}
                    sx={{
                      position: 'relative',
                      width: 48,
                      height: 48,
                      borderRadius: '18px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      background: '#fff',
                      cursor: 'pointer',
                      boxShadow: '0px 8px 12px 0px #00000014',
                    }}
                  >
                    <EmtIcon
                      className="emt-arrow-left"
                      sx={{
                        color: (theme) => theme.palette.purple.dark,
                        fontSize: '20px',
                      }}
                    />
                  </Box>
                  <EmtTypography variant="h3" fontWeight="800" color="white" lineHeight="1">
                    {t('search.findPetSitter')}
                  </EmtTypography>
                </Box>
              )}
              <NewSearch
                loading={newSearchLoading}
                onNext={handleNewSearchNext}
                newSearchInitialStep={newSearchInitialStep}
                setNewSearchInitialStep={setNewSearchInitialStep}
              />
            </Container>
          </Box>
        )}
        {/* END Search infos */}

        <EmtHeader
          background="/theme/header-pattern.png"
          backgroundColor="#762D8A"
          sx={{ paddingBottom: isMobile ? '15px' : '210px!important' }}
          noRNMargin
        >
          <Box
            sx={{
              display: isMobile ? 'flex' : 'initial',
              flexDirection: isMobile ? 'column' : 'initial',
              gap: '20px',
            }}
            className={!!currentUser ? 'EmtReactNativeWebViewMarginTop' : ''}
          >
            <Box ref={titleRef} sx={{ display: 'flex', gap: '17px', alignItems: 'center' }}>
              <Box
                onClick={() => setShowNewSearch(true)}
                sx={{
                  position: 'relative',
                  width: 48,
                  height: 48,
                  borderRadius: '18px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0px 8px 12px 0px #00000014',
                  flexShrink: 0,
                }}
              >
                <EmtIcon
                  className="emt-arrow-left"
                  sx={{
                    color: (theme) => theme.palette.purple.dark,
                    fontSize: '20px',
                  }}
                />
              </Box>
              <Box>
                <EmtTypography
                  variant="h3"
                  fontWeight={isMobile ? '700' : '800'}
                  color="white"
                  lineHeight="1"
                  sx={{ fontSize: isMobile ? '18px' : '34px' }}
                >
                  {t('search.findPetSitter')}
                </EmtTypography>
                {isMobile &&
                  (isLoading ? (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        gap: '15px',
                        flexDirection: 'column',
                        alignSelf: 'flex-start',
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="200px"
                        height={27}
                        sx={{ borderRadius: '8px', maxWidth: '100%' }}
                      />
                    </Box>
                  ) : (
                    <EmtTypography variant="h4" fontWeight="800" color="white" lineHeight="1">
                      {totalItems} résultat{totalItems !== 1 ? 's' : ''}{' '}
                      <span style={{ fontSize: '18px', fontWeight: '400' }}>
                        ({filtersValues.distance?.max ?? 0} km)
                      </span>
                    </EmtTypography>
                  ))}
              </Box>
            </Box>

            {isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                {/* Service short filter */}

                <Box
                  sx={{ width: '100%', cursor: 'pointer' }}
                  onClick={() => {
                    setShortFilterValue('base')
                    setActiveFilterSidebar(true)
                  }}
                >
                  <ReservationDates
                    variant="showService"
                    // @ts-expect-error
                    dates={dates}
                    icon={selectedService ? serviceConfigs.find((s) => s.slug === selectedService)?.iconClass : ''}
                    actionText={() => {
                      setShortFilterValue('base')
                      setActiveFilterSidebar(true)
                    }}
                    sx={{ backgroundColor: '#fff' }}
                  />
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  {/* Address short filter */}
                  <Box sx={{ width: '100%', minWidth: '0' }}>
                    <EmtClickBox
                      variant="compact"
                      value={(address?.formatted as string) || 'Sélectionner'}
                      label="Lieu"
                      iconLeftClassName="emt-pin-1"
                      iconClassName="emt-right-1"
                      onClick={() => {
                        setShortFilterValue('distance')
                        setActiveFilterSidebar(true)
                      }}
                    />
                  </Box>

                  {/* Pets short filter */}
                  <Box sx={{ width: '100%', minWidth: '0' }}>
                    <EmtClickBox
                      variant="compact"
                      value={getPetsSummary(petsToComplete, t)}
                      label="Animaux"
                      iconLeftClassName="emt-cat"
                      iconClassName="emt-right-1"
                      onClick={() => {
                        setShortFilterValue('pets')
                        setActiveFilterSidebar(true)
                      }}
                    />
                  </Box>

                  {/* Filters button */}
                  <EmtButton
                    color="purple"
                    variant="outlined"
                    startIcon={<EmtIcon className={'emt-adjust-horizontal'} />}
                    disabled={isLoading}
                    sx={{
                      fontWeight: '600',
                      display: 'flex',
                      gap: '5px',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      width: '47px',
                      height: '44px',
                      minWidth: '0',
                      flexShrink: 1,
                      backgroundColor: '#fff!important',
                      borderColor: '#fff!important',
                      '& .MuiButton-startIcon, .emt-icons': { margin: '0!important', fontSize: '15px!important' },
                    }}
                    onClick={() => {
                      setShortFilterValue('base')
                      toggleActiveFilter()
                    }}
                  >
                    {count ? (
                      <Box
                        sx={{
                          backgroundColor: '#AE59C6',
                          color: '#fff',
                          border: '1px solid #fff',
                          borderRadius: '100%',
                          width: 20,
                          height: 20,
                          position: 'absolute',
                          right: -8,
                          top: -8,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <EmtTypography variant="body2" fontSize="12px" lineHeight="0">
                          {count}
                        </EmtTypography>
                      </Box>
                    ) : null}
                  </EmtButton>
                </Box>
              </Box>
            )}
          </Box>
        </EmtHeader>

        <Container
          sx={{
            paddingTop: isMobile ? '0' : '30px',
            paddingBottom: isMobile ? '240px' : 0,
            display: 'flex',
            flexDirection: 'column',
            rowGap: isMobile ? '15px' : '30px',
            position: 'relative',
            backgroundColor: '#F6F6F6',
            maxHeight: '100%',
            pointerEvents: showNewSearch || activeFilterSidebar ? 'none' : 'initial',
            filter: showNewSearch ? 'brightness(0.5)' : 'none',
          }}
          maxWidth={false}
        >
          {isMobile && <EmtSeparator />}
          <Box
            ref={containerRef}
            sx={{
              width: '100%',
              marginTop: isMobile ? '0' : '-223px',
              backgroundColor: '#F6F6F6',
              padding: isMobile ? '0' : '20px',
              borderRadius: '18px',
              height: isMobile ? 'unset' : containerHeight,
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
            }}
          >
            {/* Mobile buttons */}
            {/* {isMobile ? (
            <Box
              sx={{
                display: 'flex',
                position: 'fixed',
                right: '8px',
                bottom: '150px',
                gap: '10px',
                zIndex: 999,
                ...transformStyles,
              }}
              className="EmtReactNativeWebViewPaddingBottom"
            >
              <EmtButton
                color="purple"
                startIcon={<OpenMapIcon color={'currentColor'} width={20} height={20} />}
                sx={{
                  fontWeight: '600',
                  display: 'flex',
                  gap: '5px',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '62px',
                  height: '62px',
                  '& .MuiButton-startIcon': { margin: '0!important' },
                }}
                onClick={() => setShowMap(true)}
              >
                <EmtTypography variant="body2">{t('search.map')}</EmtTypography>
              </EmtButton>
              <EmtButton
                color="purple"
                variant="outlined"
                startIcon={<EmtIcon className={'emt-adjust-horizontal'} />}
                sx={{
                  fontWeight: '600',
                  display: 'flex',
                  gap: '5px',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '62px',
                  height: '62px',
                  backgroundColor: '#fff!important',
                  '& .MuiButton-startIcon, .emt-icons': { margin: '0!important', fontSize: '18px!important' },
                }}
                onClick={toggleActiveFilter}
              >
                <EmtTypography variant="body2">{t('search.filters')}</EmtTypography>
              </EmtButton>
            </Box>
          ) : null} */}

            {isMobile && (
              <Tabs
                sx={{
                  '& .MuiTabs-flexContainer': {
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiTabs-indicator': {
                    width: '50%',
                    backgroundColor: '#F7EEF9',
                    height: '100%',
                    borderRadius: '12px',
                    border: '1px solid #AE59C6',
                  },
                  '& .Mui-selected': {
                    color: '#AE59C6!important',
                  },
                }}
                value={tabValue}
                onChange={handleTabChange}
              >
                <Tab
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    justifyContent: 'center',
                    color: '#34303E!important',
                    fontSize: '14px!important',
                    fontWeight: '700!important',
                    textTransform: 'none',
                    height: '48px!important',
                    maxWidth: 'unset!important',
                    minHeight: 'unset!important',
                    zIndex: 1,
                  }}
                  label={'Liste'}
                  icon={<EmtIcon sx={{ marginBottom: '0!important' }} className={'emt-list-layout-1'} />}
                  onClick={() => setShowMap(false)}
                />
                <Tab
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    justifyContent: 'center',
                    color: '#34303E!important',
                    fontSize: '14px!important',
                    fontWeight: '700!important',
                    textTransform: 'none',
                    height: '48px!important',
                    maxWidth: 'unset!important',
                    minHeight: 'unset!important',
                    zIndex: 1,
                  }}
                  label={'Carte'}
                  icon={<OpenMapIcon color={'currentColor'} width={16} height={16} />}
                  onClick={() => setShowMap(true)}
                />
              </Tabs>
            )}

            {((!showMap && !isMobile) || !isMobile) && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '10px',
                  backgroundColor: '#F6F6F6',
                  borderRadius: isMobile ? '18px 18px 0 0' : '16px',
                  marginLeft: isMobile ? '-16px' : '0',
                  marginRight: isMobile ? '-16px' : '0',
                  height: isMobile ? 'auto' : '52px',
                }}
              >
                {isMobile && (
                  <Box
                    sx={{
                      background: 'linear-gradient(270deg, #F9F9F9 20.49%, rgba(249, 249, 249, 0) 107.87%)',
                      position: 'absolute',
                      zIndex: 99,
                      right: 0,
                      height: '100%',
                      width: '57px',
                    }}
                  />
                )}

                {!isMobile && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      overflowX: isMobile ? 'auto' : 'visible',
                      paddingRight: isMobile ? '40px' : '0',
                      paddingLeft: isMobile ? '15px' : '0',
                      height: '100%',
                    }}
                  >
                    {/* Service short filter */}

                    <Box
                      sx={{
                        flexShrink: isMobile ? 0 : 'initial',
                        width: isMobile ? '200px' : '430px',
                        maxWidth: isMobile ? 'unset' : '32vw',
                        height: '100%',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setShortFilterValue('base')
                        setActiveFilterSidebar(true)
                      }}
                    >
                      <ReservationDates
                        variant="showService"
                        // @ts-expect-error
                        dates={dates}
                        icon={selectedService ? serviceConfigs.find((s) => s.slug === selectedService)?.iconClass : ''}
                        actionText={() => {
                          setShortFilterValue('base')
                          setActiveFilterSidebar(true)
                        }}
                        sx={{ backgroundColor: '#fff', paddingLeft: '6px' }}
                      />
                    </Box>

                    {/* Address short filter */}
                    <Box
                      sx={{
                        flexShrink: isMobile ? 0 : 'initial',
                        width: isMobile ? '200px' : '230px',
                        maxWidth: isMobile ? 'unset' : '18vw',
                        height: '100%',
                      }}
                    >
                      <EmtClickBox
                        value={(address?.formatted as string) || 'Sélectionner'}
                        label="Lieu"
                        iconClassName="emt-right-1"
                        onClick={() => {
                          setShortFilterValue('distance')
                          setActiveFilterSidebar(true)
                        }}
                      />
                    </Box>

                    {/* Pets short filter */}
                    <Box
                      sx={{
                        flexShrink: isMobile ? 0 : 'initial',
                        width: isMobile ? '200px' : '230px',
                        maxWidth: isMobile ? 'unset' : '18vw',
                        height: '100%',
                      }}
                    >
                      <EmtClickBox
                        value={getPetsSummary(petsToComplete, t)}
                        label="Animaux"
                        iconClassName="emt-right-1"
                        onClick={() => {
                          setShortFilterValue('pets')
                          setActiveFilterSidebar(true)
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {!isMobile && (
                  <EmtButton
                    startIcon={<EmtIcon className={'emt-adjust-horizontal-1'} />}
                    variant="outlined"
                    color="purple"
                    sx={{ padding: '12px 16px', position: 'relative' }}
                    onClick={toggleActiveFilter}
                  >
                    {t('search.allFilters')}
                    {count ? (
                      <Box
                        sx={{
                          backgroundColor: '#AE59C6',
                          color: '#fff',
                          borderRadius: '100%',
                          width: 20,
                          height: 20,
                          position: 'absolute',
                          right: -8,
                          top: -8,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <EmtTypography variant="body2" fontSize="12px" lineHeight="0">
                          {count}
                        </EmtTypography>
                      </Box>
                    ) : null}
                  </EmtButton>
                )}
              </Box>
            )}

            {/* Petsitters selection */}
            <PetsittersSelection ref={petsittersSelectionRef} openFloatingProfile={floating.handleOpenProfile} />
            <Box
              sx={{
                display: isMobile ? 'flex' : 'grid',
                gridTemplateColumns: gridLayout,
                gap: '10px',
                height: `calc(100% - 67px)`,
              }}
            >
              {childWidth && (
                <>
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: isMobile ? '15px' : '0',
                      width: '100%',
                      height: '0',
                      minHeight: '100%',
                    }}
                  >
                    <Box
                      ref={petSittersContainerRef}
                      sx={{
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                        width: '100%',
                        height: '100%',
                        paddingBottom: '60px',
                      }}
                    >
                      {petSitterStates?.length === 0 && !isLoading ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            flexDirection: 'column',
                            gap: '20px',
                            textAlign: 'center',
                          }}
                        >
                          <NextImage
                            src={VariantImages.NoPetSitter}
                            width={380}
                            style={{ maxWidth: '100%' }}
                            alt="No pet sitter found"
                          />
                          <EmtTypography variant="body1" fontWeight="700">
                            {t('search.noPetsitter.title')}
                          </EmtTypography>
                          <EmtTypography variant="body2">{t('search.noPetsitter.description')}</EmtTypography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                            <EmtButton
                              startIcon={<EmtIcon className={'emt-anti-clockwise'} />}
                              endIcon={
                                count ? (
                                  <Box
                                    sx={{
                                      backgroundColor: '#AE59C6',
                                      color: '#fff',
                                      borderRadius: '100%',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: 20,
                                      width: 20,
                                    }}
                                  >
                                    <EmtTypography variant="body2" fontSize="12px" lineHeight="0">
                                      {count}
                                    </EmtTypography>
                                  </Box>
                                ) : null
                              }
                              variant="outlined"
                              color="purple"
                              sx={{ padding: '12px 16px', position: 'relative', marginBottom: '5px' }}
                              onClick={() => handleSearchParamsChange(initialFiltersValues)}
                            >
                              {t('search.reinitiateFilters')}
                            </EmtButton>
                            <EmtTypography variant="caption" color="text.secondary">
                              {t('search.noPetsitter.buttonDescription')}
                            </EmtTypography>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {!isMobile &&
                            (isLoading ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  display: 'flex',
                                  gap: '15px',
                                  flexDirection: 'column',
                                  alignSelf: 'flex-start',
                                }}
                              >
                                <Skeleton
                                  variant="rectangular"
                                  width="300px"
                                  height={27}
                                  sx={{ borderRadius: '8px', maxWidth: '100%' }}
                                />
                              </Box>
                            ) : (
                              <EmtTypography variant="body1" fontWeight="700" fontSize="18px">
                                {t('search.petSittersNearYou', {
                                  count: totalItems,
                                  distance: filtersValues.distance?.max ?? 0,
                                })}
                              </EmtTypography>
                            ))}

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                              backgroundColor: '#fff',
                              borderRadius: '16px',
                              padding: '16px',
                            }}
                          >
                            <EmtIcon
                              className="emt-info-circle"
                              sx={{
                                color: '#F9F9F9',
                                backgroundColor: '#4A4754',
                                borderRadius: '50%',
                                padding: '0px',
                                fontSize: '20px',
                                border: '1px solid #4A4754',
                              }}
                            />
                            <EmtTypography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                              <Trans
                                t={t}
                                i18nKey={'search.testInfoBoxText'}
                                components={{
                                  IconSkill: (
                                    <EmtIcon sx={{ display: 'inline', color: '#0030cc' }} className="emt-knowledge" />
                                  ),
                                  IconPsychological: (
                                    <EmtIcon sx={{ display: 'inline', color: '#C80068' }} className="emt-psychology" />
                                  ),
                                  strong: <strong />,
                                  BlueInseparables: (
                                    <span
                                      style={{
                                        whiteSpace: 'nowrap',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: '#0030cc',
                                        fontWeight: '700',
                                        lineHeight: '0',
                                        height: '0px',
                                        transform: 'translateY(2px)',
                                      }}
                                    />
                                  ),
                                  PinkInseparables: (
                                    <span
                                      style={{
                                        whiteSpace: 'nowrap',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: '#C80068',
                                        fontWeight: '700',
                                        lineHeight: '0',
                                        height: '0px',
                                        transform: 'translateY(2px)',
                                      }}
                                    />
                                  ),
                                }}
                              />
                            </EmtTypography>
                          </Box>

                          {isLoading ? (
                            <Box
                              sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '15px',
                                flexDirection: 'column',
                              }}
                            >
                              <Skeleton variant="rectangular" width="100%" height={216} sx={{ borderRadius: '18px' }} />
                              <Skeleton variant="rectangular" width="100%" height={216} sx={{ borderRadius: '18px' }} />
                              <Skeleton variant="rectangular" width="100%" height={216} sx={{ borderRadius: '18px' }} />
                            </Box>
                          ) : (
                            petSitterStates?.map((petSitter: any) => (
                              <Box
                                sx={{ width: '100%' }}
                                key={petSitter.uuid}
                                ref={(el) => {
                                  if (el) petSitterRefs.current[petSitter.uuid] = el as HTMLDivElement | null
                                }}
                              >
                                <PetSitterCard
                                  data={petSitter}
                                  active={petSitter.uuid === selectedPetSitterId}
                                  select={!petsittersIds.includes(petSitter.uuid)}
                                  remove={petsittersIds.includes(petSitter.uuid)}
                                  seeProfile
                                  onMouseOver={() => handleCardHover(petSitter.uuid)}
                                  style={{ width: '100%', cursor: 'pointer' }}
                                  selectedService={searchParams.services}
                                  onMobileNavigate={floating.handleOpenProfile}
                                  selectClickOverride={!currentUser ? () => setSignUpModalOpen(true) : undefined}
                                />
                              </Box>
                            ))
                          )}

                          {/* See more */}
                          <Box sx={{ margin: 'auto' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <EmtTypography variant="body2" marginBottom="15px">
                                {t('search.viewedPetSitters', { viewed: petSitterStates.length, total: totalItems })}
                              </EmtTypography>
                              <EmtProgressBar
                                value={totalItems ? (petSitterStates.length / totalItems) * 100 : 0}
                                color="purple"
                              />
                            </Box>
                            <EmtButton
                              color="purple"
                              sx={{ marginTop: '23px', ...(isMobile ? {} : { padding: '6px 40px' }) }}
                              fullWidth={isMobile}
                              onClick={handleLoadMore}
                              disabled={!hasMore || isFetchingNextPage}
                            >
                              {isFetchingNextPage ? t('search.loading') : t('search.loadMoreProfiles')}
                            </EmtButton>
                          </Box>
                          {/* END See more */}
                        </>
                      )}
                    </Box>
                  </Box>

                  {showMap && (
                    <>
                      <Box
                        ref={mapContainerRef}
                        sx={{
                          position: isMobile ? 'fixed' : 'relative',
                          top: isMobile ? '0' : 'auto',
                          right: isMobile ? '0' : 'auto',
                          height: isMobile ? '100vh' : '100%',
                          minHeight: '100%',
                          width: isMobile ? '100vw' : 'auto',
                          borderRadius: isMobile ? '0' : '0 18px 0 0',
                          overflow: 'hidden',
                          zIndex: isMobile ? 1198 : 'unset',
                        }}
                      >
                        {!isMobile && activeSearchButton ? (
                          <Box
                            sx={{
                              zIndex: 1200,
                              position: 'absolute',
                              top: 15,
                              left: '50%',
                              transform: 'translateX(-50%)',
                            }}
                          >
                            <EmtButton
                              sx={{
                                width: '200px',
                                padding: '6px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                '& .emt-icons': { width: 10 },
                              }}
                              color="purple"
                              startIcon={<EmtIcon className="emt-anti-clockwise-1" />}
                              loader={isFetching}
                              onClick={onSearchAreaClick}
                            >
                              {t('search.searchArea')}
                            </EmtButton>
                          </Box>
                        ) : null}

                        <EmtMap
                          ref={mapInstanceRef}
                          zoom={mapZoom}
                          lng={coordinates.lng}
                          lat={coordinates.lat}
                          onMapDragEnd={onMapDragEnd}
                          onMapZoomEnd={onMapZoom}
                          markerList={markers}
                          accessToken={Constants.mapboxAccessToken}
                          minZoom={5}
                          maxZoom={14}
                          navigationControl
                          sx={{ width: '100%' }}
                          resizeTrigger={resizeTrigger}
                          onFlyToEnd={() => {
                            if (!pendingCloseAfterFlyRef.current) return
                            pendingCloseAfterFlyRef.current = false

                            setShowNewSearch(false)
                            setNewSearchLoading(false)
                          }}
                          disableNextCenterAnimation={disableNextCenterAnimation}
                          onCenterApplied={() => {
                            // consume the "one-shot" flag
                            if (disableNextCenterAnimationRef.current) {
                              disableNextCenterAnimationRef.current = false
                              setDisableNextCenterAnimation(false)
                            }
                          }}
                        />
                      </Box>

                      {isMobile && (
                        <Box>
                          <Box
                            onClick={() => {
                              setShowMap(false)
                              setTabValue(0)
                            }}
                            sx={{
                              width: 48,
                              height: 48,
                              overflow: 'hidden',
                              borderRadius: '18px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexShrink: '0',
                              cursor: 'pointer',
                              zIndex: 1200,
                              position: 'fixed',
                              top: 20,
                              left: 20,
                            }}
                            className="EmtReactNativeWebViewMarginTop"
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                background: '#fff',
                                pointerEvents: 'none',
                                boxShadow: '0px 8px 12px 0px #00000014',
                              }}
                            />
                            <EmtIcon
                              className={'emt-arrow-left'}
                              sx={{
                                color: (theme: any) => (theme.palette as any).purple.dark,
                                fontSize: '20px',
                                zIndex: 1,
                                position: 'absolute',
                              }}
                            />
                          </Box>

                          <EmtButton
                            startIcon={<EmtIcon className={'emt-adjust-horizontal-1'} />}
                            variant="outlined"
                            color="purple"
                            sx={{
                              padding: '12px 16px',
                              zIndex: 1200,
                              position: 'fixed',
                              top: '20px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                            }}
                            className="EmtReactNativeWebViewMarginTop"
                            onClick={toggleActiveFilter}
                          >
                            {t('search.filters')}
                            {count ? (
                              <Box
                                sx={{
                                  backgroundColor: '#AE59C6',
                                  color: '#fff',
                                  borderRadius: '100%',
                                  width: 20,
                                  height: 20,
                                  position: 'absolute',
                                  right: -8,
                                  top: -8,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <EmtTypography variant="body2" fontSize="12px" lineHeight="0">
                                  {count}
                                </EmtTypography>
                              </Box>
                            ) : null}
                          </EmtButton>

                          {activeSearchButton ? (
                            <Box
                              sx={{
                                padding: '12px 16px',
                                zIndex: 1200,
                                position: 'fixed',
                                top: 76,
                                left: '50%',
                                transform: 'translateX(-50%)',
                              }}
                              className="EmtReactNativeWebViewMarginTop"
                            >
                              <EmtButton
                                sx={{
                                  width: '200px',
                                  padding: '6px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  '& .emt-icons': { width: 10 },
                                }}
                                color="purple"
                                startIcon={<EmtIcon className="emt-anti-clockwise-1" />}
                                loader={isFetching}
                                onClick={onSearchAreaClick}
                              >
                                {t('search.searchArea')}
                              </EmtButton>
                            </Box>
                          ) : null}
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Container>

        {/* Mobile ask sitter */}
        {isMobile && showMap && (
          <MobileSwipeMenu
            title={`Les ${petSitterStates.length} meilleurs résultats` as string}
            subtitle={t('search.moveMapToSeeMore') as string}
            isLoading={isLoading}
            openColor="#F6F6F6"
          >
            <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {petSitterStates?.map((petSitter: any) => (
                <Box
                  sx={{ width: '100%' }}
                  key={petSitter.uuid}
                  ref={(el) => {
                    if (el) petSitterRefs.current[petSitter.uuid] = el as HTMLDivElement
                  }}
                >
                  <PetSitterCard
                    data={petSitter}
                    active={petSitter.uuid === selectedPetSitterId}
                    select={!petsittersIds.includes(petSitter.uuid)}
                    remove={petsittersIds.includes(petSitter.uuid)}
                    seeProfile
                    onMouseOver={() => handleCardHover(petSitter.uuid)}
                    style={{ width: '100%', cursor: 'pointer' }}
                    selectedService={searchParams.services}
                    onMobileNavigate={floating.handleOpenProfile}
                    selectClickOverride={!currentUser ? () => setSignUpModalOpen(true) : undefined}
                  />
                </Box>
              ))}
            </Box>
          </MobileSwipeMenu>
        )}

        {/* Mobile selected petsitter card */}
        {isMobile && showMap && selectedPetSitter && (
          <Box
            sx={{
              position: 'fixed',
              bottom: '120px',
              left: 0,
              right: 0,
              padding: '0 20px',
              zIndex: 1199,
            }}
            className="EmtReactNativeWebViewPaddingBottom"
          >
            <PetSitterCard
              data={selectedPetSitter as any}
              select={!petsittersIds.includes((selectedPetSitter as any).uuid)}
              remove={petsittersIds.includes((selectedPetSitter as any).uuid)}
              seeProfile
              style={{ width: '100%' }}
              selectedService={searchParams.services}
              onMobileNavigate={floating.handleOpenProfile}
              selectClickOverride={!currentUser ? () => setSignUpModalOpen(true) : undefined}
            />
          </Box>
        )}
      </Box>

      {/* Drawer + click outside close */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1203, // doit être >= drawer
          pointerEvents: activeFilterSidebar ? 'auto' : 'none',
        }}
      >
        {/* Overlay (click outside) */}
        <Box
          onClick={async () => {
            if (!activeFilterSidebar) return

            await filtersRef.current?.submitDraft()
            setActiveFilterSidebar(false)
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.0)',
          }}
        />

        <EmtDrawer
          onTop={true}
          anchor={isMobile ? 'bottom' : 'left'}
          sx={{
            borderRadius: 0,
            paddingRight: 0,
            zIndex: 1204,
            backgroundColor: isMobile ? 'transparent' : '#F6F6F6',
            paddingLeft: '20px',
            maxWidth: isMobile ? '100%' : '480px',
          }}
          open={activeFilterSidebar}
          onClose={toggleActiveFilter}
          data-filters-container
        >
          <Box onClick={(e) => e.stopPropagation()}>
            <Filters
              ref={filtersRef}
              manualRequest
              filtersValues={filtersValues}
              onFiltersValuesChange={handleFiltersValuesChange}
              shortFilter={shortFilterValue as any}
              onEraseFilter={() => {
                handleSearchParamsChange(initialFiltersValues)
              }}
              onSearchButtonClick={(submitted) => handleSearchClick(submitted)}
              activeFilterSidebar={activeFilterSidebar}
              onClose={toggleActiveFilter}
              isLoading={isFetching}
            />
          </Box>
        </EmtDrawer>
      </Box>

      {/* <SearchPetSitterTipsModal
        // @ts-expect-error
        filters={modalFilters}
        services={services}
        pets={pets}
        onFiltersChange={handleModalFiltersChange}
        onSearch={handleSearchClick}
      /> */}

      {/* MOBILE PROFILE MODAL */}
      <FloatingProfileModal
        isMobile={floating.isMobile}
        open={floating.profileIsOpen}
        customerId={floating.customerId}
        profileKind={floating.profileKind}
        onClose={floating.handleCloseProfile}
      />

      {/* New Search Modal */}

      <EmtModal
        open={showNewSearch && isMobile}
        // {...(!newSearchInitialStep && {
        //   onClose: () => setNewSearchInitialStep(true),
        // })}
        fullScreenMobile={!newSearchInitialStep}
        sx={{
          zIndex: newSearchInitialStep ? 905 : 1205,
          '& .inner-box': {
            width: '100vw',
            maxWidth: '100vw',
            backgroundColor: '#F6F6F6',
            paddingTop: currentUser ? '30px' : '0',
            // paddingBottom: '120px',
            ...(newSearchInitialStep && {
              paddingBottom: '30px',
              top: '0',
              transform: 'translate(-50%, 0)',
              borderRadius: '0 0 18px 18px',
            }),
            ...(isMobile &&
              newSearchInitialStep && {
                background: `url('/theme/header-pattern.png'), #762D8A`,
                backgroundColor: '#762D8A',
                backgroundRepeat: 'repeat',
                backgroundSize: '80%',
                '&::after': {
                  content: "''",
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(157.28deg, rgba(138, 53, 162, 0) 0.69%, #8A35A2 100%)',
                  zIndex: -1,
                },
              }),
          },
          '& .emt-x': {
            top: '25px',
          },
        }}
      >
        <NewSearch
          loading={newSearchLoading}
          onNext={handleNewSearchNext}
          newSearchInitialStep={newSearchInitialStep}
          setNewSearchInitialStep={setNewSearchInitialStep}
        />
      </EmtModal>
      {/* END New Search Modal */}

      {/* SIGN UP MODAL */}
      <SignUpModal open={signUpModalOpen} onClose={() => setSignUpModalOpen(false)} />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const base = await getEMTServerSideProps({
    namespaces: ['common', 'home', 'profile', 'message', 'petsitter', 'connection', 'search'],
    i18nTitleNs: 'home',
    i18nTitleKey: 'title',
  })(ctx as any)

  if ('redirect' in base || 'notFound' in base) return base

  const ip = getClientIp(ctx.req)
  const byIp = await fetchIpApi(ip)

  const defaultAddress = byIp ?? PARIS_CENTER

  return {
    ...base,
    props: {
      ...(base as any).props,
      defaultAddress,
    },
  }
}

export default withPublicRoute()(SearchPage)
